import React from 'react';
import classNames from 'classnames';
import {
  Typography,
  TypographyType,
  TypographyVariant,
  TextBox,
  Badge,
} from 'src/components-dummy';
import { ExperimentMetric, WizardMetricBaseline } from 'src/services';
import { useStepControlsSync } from '../useStepControlsSync';
import {
  TIER_A_METRICS,
  TIER_B_METRICS,
  buildSensitivityCopy,
  formatBaselineValue,
} from './constants';
import { useGoalStep } from './useGoalStep';
import { GoalStepProps, SensitivityInfo } from './types';
import './GoalStep.scss';

function getSensitivityText(
  selectedMetricLabel: string | undefined,
  isLoadingSensitivity: boolean,
  sensitivity: SensitivityInfo | undefined
): string {
  if (!selectedMetricLabel) {
    return 'Select a metric above to see its sensitivity.';
  }
  if (isLoadingSensitivity) {
    return 'Calculating sensitivity for this metric...';
  }
  if (sensitivity?.status === 'ok') {
    return buildSensitivityCopy(
      selectedMetricLabel,
      sensitivity.dailyTestUsers ?? 0,
      sensitivity.minEffectPercent ?? 0
    );
  }
  if (sensitivity?.status === 'unavailable') {
    return `Sensitivity unavailable: ${sensitivity.reason}`;
  }
  return '';
}

function renderMetricCardBaseline(
  metric: ExperimentMetric,
  baseline: WizardMetricBaseline | undefined,
  isLoadingBaselines: boolean,
  isSoon: boolean
): JSX.Element | null {
  if (isLoadingBaselines) {
    return (
      <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
        Loading baseline...
      </Typography>
    );
  }
  if (isSoon) {
    return <Badge text='Soon' />;
  }
  if (baseline?.hasBaseline) {
    return (
      <Typography type={TypographyType.Body} variant={TypographyVariant.SmallBold}>
        Baseline: {formatBaselineValue(metric, baseline.value as number)}
      </Typography>
    );
  }
  if (baseline) {
    return (
      <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
        No baseline yet
      </Typography>
    );
  }
  return null;
}

export const GoalStep = (props: GoalStepProps): JSX.Element => {
  const { onStepControlsChange } = props;
  const {
    baselines,
    isLoadingBaselines,
    selectedMetric,
    hypothesis,
    name,
    nameError,
    selectMetric,
    setHypothesis,
    setName,
    onSubmit,
    isSubmitting,
    canContinue,
    sensitivity,
    isLoadingSensitivity,
  } = useGoalStep(props);

  useStepControlsSync(
    { canContinue, isSubmitting, onSubmit, selectedMetric },
    onStepControlsChange,
    [canContinue, isSubmitting, onSubmit, selectedMetric]
  );

  const selectedMetricLabel = TIER_A_METRICS.find(({ metric }) => metric === selectedMetric)?.label;

  return (
    <div className='syte-goal-step'>
      <Typography type={TypographyType.Heading} variant={TypographyVariant.MediumBold}>
        What do you want to improve?
      </Typography>

      <div className='syte-goal-step-cards'>
        {TIER_A_METRICS.map(({ metric, label, description }) => {
          const baseline = baselines.find(item => item.metric === metric);
          const isSoon = baseline?.status === 'soon';

          return (
            <button
              key={metric}
              type='button'
              disabled={isSoon}
              className={classNames('syte-goal-step-card', {
                'syte-goal-step-card-selected': selectedMetric === metric,
                'syte-goal-step-card-disabled': isSoon,
              })}
              onClick={() => selectMetric(metric)}
            >
              <Typography type={TypographyType.Body} variant={TypographyVariant.MediumBold}>
                {label}
              </Typography>
              <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
                {description}
              </Typography>
              <div className='syte-goal-step-card-baseline'>
                {renderMetricCardBaseline(metric, baseline, isLoadingBaselines, isSoon)}
              </div>
            </button>
          );
        })}

        {TIER_B_METRICS.map(({ key, label, description }) => (
          <div key={key} className='syte-goal-step-card syte-goal-step-card-disabled'>
            <Typography type={TypographyType.Body} variant={TypographyVariant.MediumBold}>
              {label}
            </Typography>
            <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
              {description}
            </Typography>
            <div className='syte-goal-step-card-baseline'>
              <Badge text='Soon' />
            </div>
          </div>
        ))}
      </div>

      <div className='syte-goal-step-card-group'>
        <div className='syte-goal-step-hypothesis'>
          <Typography type={TypographyType.Body} variant={TypographyVariant.MediumBold}>
            Hypothesis
          </Typography>
          <TextBox
            multiline
            maxLength={240}
            value={hypothesis}
            onChange={setHypothesis}
            disabled={!selectedMetric}
            placeholder='Select a metric above to generate a hypothesis'
          />
        </div>

        <div className='syte-goal-step-sensitivity'>
          <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
            {getSensitivityText(selectedMetricLabel, isLoadingSensitivity, sensitivity)}
          </Typography>
        </div>

        <div className='syte-goal-step-name'>
          <TextBox label='Experiment Name' value={name} onChange={setName} error={nameError} />
        </div>
      </div>
    </div>
  );
};
